import { CdkContextMenuTrigger } from '@angular/cdk/menu';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { EChartOption, SiChartComponent } from '@siemens/charts-ng';
import { SiCardModule } from '@siemens/element-ng/card';
import { SiFormModule } from '@siemens/element-ng/form';
import { SiInlineNotificationComponent } from '@siemens/element-ng/inline-notification';
import { SiMenuModule } from '@siemens/element-ng/menu';
import { SiResizeObserverDirective } from '@siemens/element-ng/resize-observer';
import { SelectItem, SiSelectModule } from '@siemens/element-ng/select';

import { SettingsService } from '../../shared/services/settings.service';
import {
  MetricType,
  MetricConfig,
  METRIC_DEFINITIONS,
  DEFAULT_ENABLED_METRICS,
  NON_METRIC_KEYS,
  MAX_Y_AXES
} from './chart.config';

// Backend API response interfaces - using snake_case to match API
interface SimulationResults {
  driving_segments?: { data?: unknown[] }[];
  stationary_segments?: { data?: unknown[] }[];
  [key: string]: unknown;
}

interface TickData {
  position_in_m?: number;
  time_in_s?: number;
  time_in_clock_format?: string;
  station_name?: string;
}

interface ResultsData {
  simulation_results?: SimulationResults;
  XTicks_plot_over_position?: TickData[];
  XTicks_plot_over_time?: TickData[];
}

@Component({
  selector: 'app-usercharts',
  imports: [
    SiCardModule,
    TranslateModule,
    SiFormModule,
    FormsModule,
    SiChartComponent,
    SiResizeObserverDirective,
    SiSelectModule,
    SiMenuModule,
    SiInlineNotificationComponent,
    CdkContextMenuTrigger
  ],
  templateUrl: './usercharts.component.html',
  styleUrl: './usercharts.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserchartsComponent {
  settingsService = inject(SettingsService);
  readonly results = input.required<ResultsData>();

  /** Reactive theme signal - directly linked to settings service */
  readonly currentTheme = this.settingsService.theme;

  // Track which chart was right-clicked for context menu
  private activeChartElement: HTMLElement | null = null;

  // Enabled metrics state - only stores which metrics are enabled
  readonly enabledMetrics = signal<Set<MetricType>>(new Set(DEFAULT_ENABLED_METRICS));

  // Computed metric list combining static definitions with dynamic enabled state
  readonly simulationMetrics = computed<MetricConfig[]>(() => {
    const enabled = this.enabledMetrics();
    return (
      Object.entries(METRIC_DEFINITIONS) as [MetricType, (typeof METRIC_DEFINITIONS)[MetricType]][]
    ).map(([key, def]) => ({
      ...def,
      value: key,
      enabled: enabled.has(key)
    }));
  });

  readonly plotType = signal<'position' | 'time'>('position');

  /**
   * Detects whether the current metric selection requires more than MAX_Y_AXES distinct units.
   * Returns the warning message or null if within limits.
   */
  readonly scaleWarning = computed<string | null>(() => {
    const enabled = this.simulationMetrics().filter(m => m.enabled);
    const distinctUnits = new Set(enabled.map(m => m.unit));
    if (distinctUnits.size > MAX_Y_AXES) {
      return `Multiple scales (${distinctUnits.size}) may reduce readability.`;
    }
    return null;
  });

  // Chart key to force re-render when metrics or plot type changes
  readonly chartKey = computed(() => {
    const metrics = this.selectedSimulationMetrics().join(',');
    const plotType = this.plotType();
    return `chart-${plotType}-${metrics}`;
  });

  /**
   * Computed signal for pre-sorted simulation data
   * This only recalculates when input data or plot type changes, not when colors/visibility change
   * Significantly improves performance for large datasets when toggling metrics
   */
  readonly sortedSimulationData = computed<unknown[]>(() => {
    const rawData = this.results()?.simulation_results;
    if (!rawData) return [];

    // Merge driving and stationary segments
    const merged = this.mergeSegments(rawData);
    const isPositionPlot = this.plotType() === 'position';

    // Sort once based on plot type
    return merged.sort((a, b) => {
      const aData = a as Record<string, unknown>;
      const bData = b as Record<string, unknown>;
      if (isPositionPlot) {
        const aPos = (aData['position_in_m'] as number) ?? 0;
        const bPos = (bData['position_in_m'] as number) ?? 0;
        if (aPos === bPos) {
          return ((aData['time_in_s'] as number) ?? 0) - ((bData['time_in_s'] as number) ?? 0);
        }
        return aPos - bPos;
      } else {
        return ((aData['time_in_s'] as number) ?? 0) - ((bData['time_in_s'] as number) ?? 0);
      }
    });
  });

  // Combined chart with multiple metrics
  readonly combinedChartOptions = computed<EChartOption | undefined>(() => {
    const plotData = this.results()?.simulation_results;

    // Access plotType to establish dependency - this ensures chart updates when plotType changes
    const currentPlotType = this.plotType();

    const xTicks =
      currentPlotType === 'position'
        ? this.results()?.XTicks_plot_over_position
        : this.results()?.XTicks_plot_over_time;

    if (!plotData || !xTicks) return undefined;

    // Ensure theme changes trigger recomputation
    this.currentTheme();

    const enabledSimMetrics = this.simulationMetrics().filter(m => m.enabled);

    if (enabledSimMetrics.length === 0) {
      return undefined;
    }

    return this.buildCombinedChart(plotData, xTicks, enabledSimMetrics);
  });

  /**
   * Derives available keys from the first data point in the simulation results.
   * Only metrics that actually exist in the received data are shown.
   */
  private readonly availableDataKeys = computed<Set<string>>(() => {
    const segments = this.results()?.simulation_results?.driving_segments;
    if (!segments?.length || !segments[0].data?.length) return new Set<string>();
    const firstDataPoint = segments[0].data[0] as Record<string, unknown>;
    return new Set(Object.keys(firstDataPoint).filter(k => !NON_METRIC_KEYS.has(k)));
  });

  /**
   * Dynamic metric options for si-select — only shows metrics present in the data.
   */
  readonly metricSelectOptions = computed<SelectItem<string>[]>(() => {
    const available = this.availableDataKeys();
    return (
      Object.entries(METRIC_DEFINITIONS) as [MetricType, (typeof METRIC_DEFINITIONS)[MetricType]][]
    )
      .filter(([key]) => available.has(key))
      .map(([key, def]) => ({
        type: 'option' as const,
        value: key,
        label: `${def.label} [${def.unit}]`,
        displayValue: `${def.label} [${def.unit}]`
      }));
  });

  /**
   * Get selected simulation metrics for si-select
   */
  readonly selectedSimulationMetrics = computed<string[]>(() => {
    return this.simulationMetrics()
      .filter(m => m.enabled)
      .map(m => m.value);
  });

  /**
   * Handle simulation metrics selection change
   */
  onSimulationMetricsChange(selectedValues: string[]): void {
    this.enabledMetrics.set(new Set(selectedValues as MetricType[]));
  }

  setActiveChart(event: MouseEvent): void {
    this.activeChartElement = (event.currentTarget as HTMLElement) ?? null;
  }

  downloadChartAsSVG(): void {
    const svgElement = this.activeChartElement?.querySelector('svg');

    if (!svgElement) return;

    // Clone the SVG to avoid modifying the original
    const clonedSvg = svgElement.cloneNode(true) as SVGElement;

    // Force all text elements to black for better visibility
    const textElements = clonedSvg.querySelectorAll('text, tspan');
    textElements.forEach(textEl => {
      (textEl as SVGTextElement).setAttribute('fill', '#000000');
      (textEl as SVGTextElement).style.fill = '#000000';
    });

    // Also handle axis lines and grid lines for better contrast
    const pathElements = clonedSvg.querySelectorAll('path, line');
    pathElements.forEach(pathEl => {
      const currentStroke = (pathEl as SVGElement).getAttribute('stroke');
      if (currentStroke && (currentStroke.includes('#fff') || currentStroke.includes('white'))) {
        (pathEl as SVGElement).setAttribute('stroke', '#333333');
      }
    });

    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(clonedSvg);
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `userchart_${this.plotType()}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Merges driving and stationary segments into a single array
   */
  private mergeSegments(plotData: SimulationResults): unknown[] {
    const drivingSegments = (plotData.driving_segments ?? []).flatMap(
      (segment: any) => segment.data ?? []
    );
    const stationarySegments = (plotData.stationary_segments ?? []).flatMap(
      (segment: any) => segment.data ?? []
    );
    return [...drivingSegments, ...stationarySegments];
  }

  /**
   * Extracts series data from load cycle data for plotting
   */
  private extractSeriesData(
    loadCycleData: unknown[],
    xAccessor: (d: Record<string, unknown>) => number,
    yAccessor: (d: Record<string, unknown>) => unknown
  ): [number, number][] {
    return loadCycleData
      .map(d => [xAccessor(d as Record<string, unknown>), yAccessor(d as Record<string, unknown>)])
      .filter(p => p[1] !== null && p[1] !== undefined) as [number, number][];
  }

  /**
   * Creates station scatter series for marking stations on charts
   * This series will be positioned at the bottom using a dedicated y-axis
   */
  private createStationScatterSeries(ticks: TickData[], xAccessor: (t: TickData) => number) {
    return {
      type: 'scatter',
      xAxisIndex: 0,
      // yAxisIndex will be set by the caller to use a dedicated invisible axis
      data: (ticks || []).map(t => [xAccessor(t), 0]), // y=0 on the dedicated axis
      symbolSize: 0, // Hide the symbol/marker
      symbol: 'circle',
      silent: true,
      label: {
        show: true,
        position: [0, 5], // [horizontal offset, vertical offset] - position below the x-axis
        formatter: (params: { dataIndex: number }) => ticks?.[params.dataIndex]?.station_name ?? '',
        rotate: 45,
        fontSize: 11,
        color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000',
        align: 'right',
        verticalAlign: 'top'
      },
      z: 999
    };
  }

  /**
   * Creates common axis configuration based on theme
   */
  private getAxisLineStyle() {
    return {
      lineStyle: { color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000' }
    };
  }

  /**
   * Creates common split line configuration based on theme
   */
  private getSplitLineStyle() {
    return {
      lineStyle: {
        color: this.currentTheme() === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'
      }
    };
  }

  /**
   * Creates common axis label configuration based on theme
   */
  private getAxisLabelStyle() {
    return {
      color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000'
    };
  }

  private getChartGridConfig() {
    return {
      containLabel: true,
      bottom: 100
    };
  }

  private getYAxisNameGap(position: 'left' | 'right') {
    return position === 'left' ? 34 : 30;
  }

  private getChartAnimationConfig() {
    return {
      animation: true,
      animationDuration: 700,
      animationEasing: 'cubicOut',
      animationDurationUpdate: 350,
      animationEasingUpdate: 'cubicOut',
      animationThreshold: 200000,
      progressive: 0,
      progressiveThreshold: 200000
    };
  }

  /**
   * Creates station mark lines for charts
   */
  private createStationMarkLines(
    ticks: TickData[],
    xAccessor: (t: TickData) => number,
    showLabel: boolean = false,
    labelFormatter?: (t: TickData) => string
  ) {
    return (ticks || []).map(t => ({
      xAxis: xAccessor(t),
      xAxisIndex: 0,
      label:
        showLabel && labelFormatter
          ? {
              formatter: labelFormatter(t),
              position: 'end',
              color: this.currentTheme() === 'dark' ? '#ffffff' : '#000000',
              fontSize: 11
            }
          : { show: false },
      lineStyle: {
        color: this.currentTheme() === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
        type: 'dashed'
      }
    }));
  }

  /**
   * Builds combined chart with multiple simulation metrics.
   * Metrics sharing the same unit are plotted on a shared Y-axis.
   * Maximum 2 Y-axes (left + right); additional units still render but trigger a warning.
   */
  private buildCombinedChart(
    plotData: SimulationResults,
    xTicks: TickData[],
    simulationMetrics: MetricConfig[]
  ): EChartOption {
    const isPositionPlot = this.plotType() === 'position';
    const xAccessor = isPositionPlot
      ? (d: Record<string, unknown>) =>
          ((d['position_in_m'] as number) ?? (d['position'] as number) ?? 0) / 1000
      : (d: Record<string, unknown>) => (d['time_in_s'] as number) ?? 0;

    // Prepare series and axis arrays
    const series: any[] = [];
    const yAxis: any[] = [];

    // Use pre-sorted data instead of sorting every time
    const loadCycleData = this.sortedSimulationData();

    // Group metrics by unit to share Y-axes
    const unitToAxisIndex = new Map<string, number>();
    const unitToMetrics = new Map<string, MetricConfig[]>();
    for (const metric of simulationMetrics) {
      const existing = unitToMetrics.get(metric.unit) ?? [];
      existing.push(metric);
      unitToMetrics.set(metric.unit, existing);
    }

    // Create one Y-axis per distinct unit (left for first, right for second, alternate after)
    const unitEntries = [...unitToMetrics.entries()];
    unitEntries.forEach(([unit, metrics], unitIndex) => {
      const position = unitIndex % 2 === 0 ? 'left' : 'right';
      const axisIndex = yAxis.length;
      unitToAxisIndex.set(unit, axisIndex);

      // Keep axis visuals neutral (same behavior as plots component)
      const axisColor = this.currentTheme() === 'dark' ? '#ffffff' : '#000000';

      // Build axis label from unit (e.g., "Force [N]" or just "[kWh]")
      const axisName = metrics.length === 1 ? `${metrics[0].label} [${unit}]` : `[${unit}]`;

      yAxis.push({
        type: 'value',
        name: axisName,
        nameLocation: 'middle',
        nameGap: this.getYAxisNameGap(position),
        nameTextStyle: {
          fontSize: 13,
          fontWeight: 'bold',
          color: axisColor
        },
        position,
        offset: unitIndex >= 2 ? (unitIndex - 1) * 60 : 0,
        axisLine: {
          show: true,
          lineStyle: { color: axisColor }
        },
        axisLabel: {
          color: axisColor
        },
        splitLine: {
          show: unitIndex === 0,
          ...this.getSplitLineStyle()
        }
      });
    });

    // Create series, pointing each to the shared axis for its unit
    simulationMetrics.forEach(metric => {
      const seriesData = this.extractSeriesData(
        loadCycleData,
        xAccessor,
        (d: Record<string, unknown>) => d[metric.dataKey]
      );

      series.push({
        type: 'line',
        name: metric.label,
        data: seriesData,
        yAxisIndex: unitToAxisIndex.get(metric.unit) ?? 0,
        symbol: 'none',
        lineStyle: { color: metric.color, width: 1.8 },
        emphasis: { lineStyle: { width: 2.8 } },
        smooth: false
      });
    });

    // Calculate max X value
    const maxX = Math.max(...series.flatMap(s => s.data.map((p: any) => p[0] ?? 0)));

    // Build X-axis configuration
    const xAxisConfig = isPositionPlot
      ? {
          position: 'top',
          axisLabel: {
            show: true,
            formatter: '{value} km',
            ...this.getAxisLabelStyle(),
            fontSize: 12,
            fontFamily: 'inherit'
          }
        }
      : {
          axisLabel: { show: false }
        };

    // Create base option
    const option: EChartOption = {
      ...this.getChartAnimationConfig(),
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'line',
          lineStyle: {
            color: this.currentTheme() === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)'
          }
        },
        backgroundColor:
          this.currentTheme() === 'dark' ? 'rgba(30, 35, 50, 0.92)' : 'rgba(255, 255, 255, 0.95)',
        borderColor: this.currentTheme() === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
        textStyle: { color: this.currentTheme() === 'dark' ? '#e0e0e0' : '#333', fontSize: 12 },
        formatter: (params: any) => {
          if (!Array.isArray(params)) return '';
          const xValue = isPositionPlot
            ? `${params[0].axisValue.toFixed(2)} km`
            : `${params[0].axisValue.toFixed(0)} s`;
          let tooltip = `<b>${xValue}</b><br/>`;
          params.forEach((p: any) => {
            tooltip += `${p.marker} ${p.seriesName}: <b>${p.value[1].toFixed(2)}</b><br/>`;
          });
          return tooltip;
        }
      },
      grid: this.getChartGridConfig(),
      xAxis: {
        type: 'value',
        min: 0,
        max: maxX,
        axisPointer: { label: { show: false } },
        axisLine: this.getAxisLineStyle(),
        splitLine: this.getSplitLineStyle(),
        ...xAxisConfig
      },
      yAxis: yAxis,
      series: series,
      backgroundColor: 'transparent',
      legend: [
        {
          left: 40,
          top: 10,
          data: simulationMetrics.map(m => m.label),
          textStyle: this.getAxisLabelStyle()
        }
      ] as any
    };

    // Add station mark lines to first series
    const filteredTicks = isPositionPlot
      ? xTicks
      : xTicks.filter((_: any, i: number) => i % 2 === 0);

    const stationMarkLines = this.createStationMarkLines(
      filteredTicks,
      isPositionPlot ? (t: any) => (t.position_in_m ?? 0) / 1000 : (t: any) => t.time_in_s ?? 0,
      !isPositionPlot,
      !isPositionPlot ? (t: any) => t.time_in_clock_format ?? '' : undefined
    );

    if (series.length > 0) {
      series[0].markLine = {
        silent: true,
        symbol: ['none', 'none'],
        data: stationMarkLines
      };
    }

    // Add station scatter markers - positioned at the bottom of the chart area
    const stationScatterSeries: any = this.createStationScatterSeries(
      filteredTicks,
      isPositionPlot ? (t: any) => (t.position_in_m ?? 0) / 1000 : (t: any) => t.time_in_s ?? 0
    );

    // Position stations on a separate invisible y-axis at the bottom
    yAxis.push({
      type: 'value',
      show: false,
      min: 0,
      max: 1,
      position: 'left'
    });

    stationScatterSeries.yAxisIndex = yAxis.length - 1; // Assign to the new y-axis
    series.push(stationScatterSeries);

    return option;
  }
}
